// src/components/Chat/ChatScreen.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiMoreVertical } from "react-icons/fi";
import "../../styles/ChatScreen.css";
import { useAuth } from "../../context/AuthContext";
import { db, storage } from "../../firebase/config";
import { connectSocket } from "../../socket/signalingClient";

import {
  collection,
  addDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import VideoCall from "../Call/VideoCall";
import AudioCall from "../Call/AudioCall";
import FooterVideoCallTrigger from "../Footer/FooterVideoCallTrigger";

/* ---------------- GROUP INFO POPUP ----------------
   - Shows group name + members
   - Admin (createdBy === currentUser.uid) can add/remove
   - Other users can Exit Group
*/
const GroupInfo = ({ groupId, currentUser, onClose }) => {
  const [group, setGroup] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [newMemberPhone, setNewMemberPhone] = useState("");
  const [loading, setLoading] = useState(true);

  const isAdmin = group?.createdBy === currentUser.uid;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "groups", groupId));
        if (!snap.exists()) {
          if (mounted) setGroup(null);
          return;
        }
        const g = snap.data();
        if (mounted) setGroup({ id: snap.id, ...g });

        // load members' profiles
        const members = Array.isArray(g.members) ? g.members : [];
        const memberProfiles = await Promise.all(
          members.map(async (uid) => {
            try {
              const uSnap = await getDoc(doc(db, "users", uid));
              return uSnap.exists() ? { uid, ...uSnap.data() } : { uid, name: uid };
            } catch {
              return { uid, name: uid };
            }
          })
        );
        if (mounted) setMembersData(memberProfiles);
      } catch (err) {
        console.error("GroupInfo load error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [groupId]);

  const normalizePhone = (raw) => {
    if (!raw) return "";
    let s = raw.replace(/[^\d+]/g, "");
    if (!s.startsWith("+")) s = "+91" + s;
    return s;
  };

  const handleAddMember = async () => {
    if (!newMemberPhone.trim()) return alert("Enter phone number");
    const phone = normalizePhone(newMemberPhone.trim());

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("phone", "==", phone));
      const res = await getDocs(q);
      if (res.empty) return alert("No user found with this phone");

      const uid = res.docs[0].id;
      if (!group) return alert("Group not loaded");
      if ((group.members || []).includes(uid)) return alert("User already in group");

      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, { members: [...(group.members || []), uid] });

      // reload small part locally
      const uSnap = await getDoc(doc(db, "users", uid));
      setMembersData((p) => [...p, uSnap.exists() ? { uid, ...uSnap.data() } : { uid }]);
      setNewMemberPhone("");
    } catch (err) {
      console.error("Add member error:", err);
      alert("Failed to add member");
    }
  };

  const handleRemoveMember = async (uidToRemove) => {
    if (!group) return;
    if (!window.confirm("Remove this member from the group?")) return;
    try {
      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, { members: (group.members || []).filter((m) => m !== uidToRemove) });
      setMembersData((p) => p.filter((m) => m.uid !== uidToRemove));
      setGroup((g) => ({ ...g, members: (g.members || []).filter((m) => m !== uidToRemove) }));
    } catch (err) {
      console.error("Remove member error:", err);
      alert("Failed to remove member");
    }
  };

  const handleExitGroup = async () => {
    if (!group) return;
    if (!window.confirm("Do you want to exit this group?")) return;
    try {
      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, { members: (group.members || []).filter((m) => m !== currentUser.uid) });
      onClose();
    } catch (err) {
      console.error("Exit group error:", err);
      alert("Failed to exit group");
    }
  };

  if (loading) {
    return (
      <div className="group-info-popup">
        <div className="group-info-box">
          <p>Loading group…</p>
        </div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="group-info-popup" onClick={onClose}>
      <div className="group-info-box" onClick={(e) => e.stopPropagation()}>
        <div className="group-info-header">
          <h3>📘 {group.groupName || group.name || "Group"}</h3>
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>

        <div className="group-info-section">
          <strong>Members ({(group.members || []).length})</strong>
          <ul className="group-members-list">
            {membersData.map((m) => (
              <li key={m.uid} className="group-member-item">
                <span className="member-name">{m.name || m.email || m.uid}</span>
                {isAdmin && m.uid !== currentUser.uid && (
                  <button className="remove-btn" onClick={() => handleRemoveMember(m.uid)}>Remove</button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {isAdmin && (
          <div className="group-info-section add-member-row">
            <input
              type="text"
              placeholder="Add member phone (e.g. +91xxxxxxxxxx)"
              value={newMemberPhone}
              onChange={(e) => setNewMemberPhone(e.target.value)}
            />
            <button onClick={handleAddMember}>Add</button>
          </div>
        )}

        {!isAdmin && (
          <div className="group-info-section">
            <button className="exit-btn" onClick={handleExitGroup}>Exit Group</button>
          </div>
        )}

      </div>
    </div>
  );
};

/* ---------------- CHAT SCREEN ---------------- */
const ChatScreen = ({ selectedChat }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [audioCallOpen, setAudioCallOpen] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [usersCache, setUsersCache] = useState({}); // uid -> name mapping for group
  const ringtoneRef = useRef(new Audio("/call-ringtone.mp3")); // <-- new ringtone ref

  /* ---------------- INCOMING AUDIO/VIDEO CALL HANDLER ---------------- */
   useEffect(() => {
    if (!currentUser?.uid) return;
    const socket = connectSocket(currentUser.uid);

    socket.on("incoming-call", (call) => {
      console.log("📞 Incoming call object:", call);
      if (!call || !call.roomId) return;

      if (call.type === "audio") {
        setAudioCallOpen(true);
        setCallOpen(false);
      } else if (call.type === "video") {
        setCallOpen(true);
        setAudioCallOpen(false);
      }
    });

    return () => {
      socket.off("incoming-call");
    };
  }, [currentUser]);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null); // sound notification

  const chatId =
    selectedChat && currentUser
      ? (selectedChat.isGroup ? selectedChat.chatId : [currentUser.uid, selectedChat.uid].sort().join("_"))
      : null;

  useEffect(() => {
    if (!selectedChat || !selectedChat.isGroup) {
      setUsersCache({});
      return;
    }
    let mounted = true;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "groups", selectedChat.chatId));
        if (!snap.exists()) return;
        const g = snap.data();
        const members = Array.isArray(g.members) ? g.members : [];
        const cache = {};
        await Promise.all(
          members.map(async (uid) => {
            try {
              const uSnap = await getDoc(doc(db, "users", uid));
              cache[uid] = uSnap.exists() ? (uSnap.data().name || uSnap.data().email || uid) : uid;
            } catch {
              cache[uid] = uid;
            }
          })
        );
        if (mounted) setUsersCache(cache);
      } catch (err) {
        console.error("usersCache load error:", err);
      }
    };
    load();
    return () => { mounted = false; };
  }, [selectedChat]);

  /* ---------------- SOCKET/MESSAGES LISTENER ---------------- */
  useEffect(() => {
    if (!selectedChat || !currentUser || !chatId) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, "messages"),
      where("chatId", "==", chatId),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(q, async (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // play notification for new messages
      arr.forEach((msg) => {
        if (msg.uid !== currentUser.uid && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
      });

      setMessages(arr);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

      // update read/delivered statuses
      arr.forEach(async (msg) => {
        if (selectedChat.isGroup) {
          if (!msg.seenBy || !msg.seenBy.includes(currentUser.uid)) {
            const msgRef = doc(db, "messages", msg.id);
            try {
              await updateDoc(msgRef, {
                seenBy: Array.isArray(msg.seenBy) ? [...msg.seenBy, currentUser.uid] : [currentUser.uid],
              });
              await updateDoc(doc(db, "groups", chatId), { [`unreadCount.${currentUser.uid}`]: 0 });
            } catch {}
          }
        } else {
          if (msg.receiverId === currentUser.uid && !msg.seen) {
            const msgRef = doc(db, "messages", msg.id);
            const chatRef = doc(db, "chats", chatId);
            if (selectedChat?.uid === msg.senderId) {
              await updateDoc(msgRef, {
                seen: true,
                status: "read",
                seenAt: serverTimestamp(),
              });
              await updateDoc(chatRef, { [`unreadCount.${currentUser.uid}`]: 0 });
            } else {
              if (msg.status !== "delivered") {
                await updateDoc(msgRef, { status: "delivered" });
              }
              const chatSnap = await getDoc(chatRef);
              if (chatSnap.exists()) {
                const chatData = chatSnap.data();
                const currentUnread = chatData?.unreadCount?.[currentUser.uid] || 0;
                await updateDoc(chatRef, { [`unreadCount.${currentUser.uid}`]: currentUnread + 1 });
              }
            }
          }
        }
      });
    });

    return () => unsub();
  }, [selectedChat, currentUser, chatId]);

  /* ---------------- SEND MESSAGE ---------------- */
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selectedChat || !currentUser || !chatId) return;

    const isGroup = !!selectedChat.isGroup;
    const receiverId = isGroup ? "group" : selectedChat.uid;

    const messageData = {
      text: text.trim(),
      uid: currentUser.uid,
      chatId,
      senderId: currentUser.uid,
      receiverId,
      type: "text",
      seen: false,
      seenAt: null,
      createdAt: serverTimestamp(),
      status: "sent",
      replyTo: replyTo ? replyTo.text : null,
    };

    try {
      await addDoc(collection(db, "messages"), messageData);

      try {
        if (!isGroup) {
          const receiverSnap = await getDoc(doc(db, "users", receiverId));
          if (receiverSnap.exists()) {
            const receiverData = receiverSnap.data();
            if (receiverData?.fcmToken) {
              await fetch("http://192.248.185.50:3002/send-message", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token: receiverData.fcmToken, title: "Nukkad", body: text }),
              });
            }
          }
        }
      } catch (err) {
        console.error("FCM notify error:", err);
      }

      if (isGroup) {
        await updateDoc(doc(db, "groups", chatId), { lastMessage: messageData.text, lastMessageTime: serverTimestamp() });
      } else {
        const chatRef = doc(db, "chats", chatId);
        const chatSnap = await getDoc(chatRef);
        const prevUnread = chatSnap.exists() ? chatSnap.data()?.unreadCount?.[receiverId] || 0 : 0;
        await setDoc(chatRef, {
          users: [currentUser.uid, receiverId],
          lastMessage: messageData.text,
          lastMessageTime: serverTimestamp(),
          unreadCount: { [receiverId]: prevUnread + 1, [currentUser.uid]: 0 },
        }, { merge: true });
      }
    } catch (err) {
      console.error("sendMessage error:", err);
      alert("Send failed");
    } finally {
      setText("");
      setReplyTo(null);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  /* ---------------- FILE ATTACHMENT ---------------- */
  const handleFileClick = () => fileInputRef.current.click();
  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file || !selectedChat || !currentUser || !chatId) return;

      const isGroup = !!selectedChat.isGroup;
      const receiverId = isGroup ? "group" : selectedChat.uid;

      const filePath = `attachments/${currentUser.uid}/${Date.now()}-${file.name}`;
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      await addDoc(collection(db, "messages"), {
        text: file.name,
        fileUrl: url,
        fileType: file.type || "file",
        uid: currentUser.uid,
        chatId,
        senderId: currentUser.uid,
        receiverId,
        type: "file",
        seen: false,
        seenAt: null,
        createdAt: serverTimestamp(),
        status: "sent",
      });

      if (isGroup) {
        await updateDoc(doc(db, "groups", chatId), { lastMessage: file.name, lastMessageTime: serverTimestamp() });
      } else {
        const chatRef = doc(db, "chats", chatId);
        const chatSnap = await getDoc(chatRef);
        const prevUnread = chatSnap.exists() ? chatSnap.data()?.unreadCount?.[receiverId] || 0 : 0;
        await setDoc(chatRef, {
          users: [currentUser.uid, receiverId],
          lastMessage: file.name,
          lastMessageTime: serverTimestamp(),
          unreadCount: { [receiverId]: prevUnread + 1, [currentUser.uid]: 0 },
        }, { merge: true });
      }

    } catch (err) {
      console.error("file upload error:", err);
      alert("Upload failed");
    } finally {
      e.target.value = "";
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  /* ---------------- TIME & STATUS ---------------- */
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    if (timestamp?.toDate) {
      const d = timestamp.toDate();
      let hours = d.getHours();
      const min = d.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${min} ${ampm}`;
    }
    return "";
  };

  const renderStatus = (msg) => {
    if (selectedChat?.isGroup) return null;
    if (msg.senderId !== currentUser.uid) return null;
    if (msg.status === "sent") return <span className="msg-status">✓</span>;
    if (msg.status === "delivered") return <span className="msg-status">✓✓</span>;
    if (msg.status === "read") return <span className="msg-status seen">✓✓</span>;
  };

  /* ---------------- RENDER ---------------- */
  const headerLabel = selectedChat ? (selectedChat.groupName || selectedChat.name || selectedChat.chatName || selectedChat.email || "Chat") : "";

  return (
    <div className="chat-container">
      <audio ref={audioRef} src="/sounds/message.mp3" preload="auto" />

      {/* Header */}
      <div className="chat-header">
        <div
          className={`chat-header-left ${selectedChat?.isGroup ? "group-header-clickable" : ""}`}
          onClick={() => { if (selectedChat?.isGroup) setShowGroupInfo(true); }}
          role={selectedChat?.isGroup ? "button" : undefined}
          tabIndex={selectedChat?.isGroup ? 0 : -1}
          onKeyDown={(e) => { if (e.key === "Enter" && selectedChat?.isGroup) setShowGroupInfo(true); }}
        >
          <div className="chat-header-name">{headerLabel}</div>
          {selectedChat?.isGroup && <div className="chat-header-sub">Group — click for info</div>}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-body scroll-body">
        {messages.map((msg) => {
          const isUser = msg.uid === currentUser?.uid;
          const menuOpen = activeMenu === msg.id;
          const senderName = selectedChat?.isGroup
            ? (usersCache[msg.senderId] || msg.senderId)
            : (isUser ? "You" : (selectedChat?.name || selectedChat?.email || selectedChat?.uid));

          return (
            <div key={msg.id} className={`chat-bubble ${isUser ? "sent" : "received"}`}>
              {msg.replyTo && <div className="reply-block">↩ {msg.replyTo}</div>}

              <div className="msg-header">
                <span className="msg-sender">{senderName}</span>
                <div className="bubble-menu">
                  <button className="msg-menu-btn" onClick={() => setActiveMenu(menuOpen ? null : msg.id)}>
                    <FiMoreVertical />
                  </button>
                  {menuOpen && (
                    <ul className="msg-dropdown">
                      <li onClick={() => setReplyTo(msg)}>Reply</li>
                      <li onClick={async () => await deleteDoc(doc(db, "messages", msg.id))}>Delete</li>
                    </ul>
                  )}
                </div>
              </div>

              <div className="msg-text">
                {msg.fileUrl ? <a href={msg.fileUrl} target="_blank" rel="noreferrer">📎 {msg.text}</a> : msg.text}
              </div>

              <div className="msg-meta">
                <span className="msg-time">{formatTime(msg.createdAt)}</span>
                {renderStatus(msg)}
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Footer */}
      <div className="chat-footer-fixed">
        {showEmoji && (
          <div className="emoji-panel">
            {["😀","😂","😍","😭","😡","👍","❤️","🔥","😁","😎","🤩"].map((em) => (
              <span key={em} onClick={() => { setText((p) => p + em); setShowEmoji(false); }}>{em}</span>
            ))}
          </div>
        )}

        <form className="chat-input" onSubmit={sendMessage}>
          <button type="button" className="input-icon" onClick={() => setShowEmoji(!showEmoji)}>😊</button>
          <button type="button" className="input-icon" onClick={handleFileClick}>📎</button>
          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />

          <input
            type="text"
            placeholder={replyTo ? `Replying: ${replyTo.text}` : "Type a message..."}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          <button type="submit" className="send-btn">➤</button>

          {!selectedChat?.isGroup && (
            <>
              <button type="button" className="call-btn" onClick={() => setCallOpen(true)}>📹 Call</button>
              <button type="button" className="call-btn" onClick={() => setAudioCallOpen(true)}>🎤 Audio</button>
            </>
          )}
        </form>
      </div>

      {callOpen && selectedChat && currentUser && (
        <VideoCall
          roomId={
            selectedChat.isGroup
              ? selectedChat.chatId
              : [currentUser.uid, selectedChat.uid].sort().join("_")
          }
          userId={currentUser.uid}
          onClose={() => {
            setCallOpen(false);
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
          }}
          ringtoneRef={ringtoneRef} // pass ringtone to VideoCall
        />
      )}

      {audioCallOpen && selectedChat && currentUser && (
        <AudioCall
          roomId={
            selectedChat.isGroup
              ? selectedChat.chatId
              : [currentUser.uid, selectedChat.uid].sort().join("_")
          }
          userId={currentUser.uid}
          onClose={() => {
            setAudioCallOpen(false);
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
          }}
          ringtoneRef={ringtoneRef}
        />
      )}

      {showGroupInfo && selectedChat?.isGroup && (
        <GroupInfo groupId={selectedChat.chatId} currentUser={currentUser} onClose={() => setShowGroupInfo(false)} />
      )}
    </div>
  );
};

export default ChatScreen;
